import type { ContentStatus } from "@/pages/Research/Cms/types";
import styles from "./LifecycleStepper.module.css";

type LifecycleStep<TStatus extends string = ContentStatus> = {
  key: TStatus;
  label: string;
  /**
   * Advance-button wording when this step is next. Defaults to `Mark ${label}`; set it where the
   * label names a state that does not read as an action.
   */
  actionLabel?: string;
};

type LifecycleStepperProps<TStatus extends string = ContentStatus> = {
  steps: LifecycleStep<TStatus>[];
  currentStatus: TStatus;
  onAdvance?: (nextStatus: TStatus) => void;
  onRevoke?: (firstStatus: TStatus) => void;
  busy?: boolean;
  disabled?: boolean;
  /**
   * Per-target permission check. Given the status a button would set, returns
   * whether the actor may set it; a `false` result disables that button on its
   * own so the user cannot fire a transition the backend would only 403. Omit to
   * leave every transition ungated.
   */
  canSetStatus?: (status: TStatus) => boolean;
};

const NO_STATUS_PERMISSION_MESSAGE = "You don't have permission to set this status.";

const LIFECYCLE_STEPS: LifecycleStep[] = [
  { key: "not_started", label: "Not Started" },
  { key: "in_progress", label: "In Progress" },
  { key: "ready_for_editing", label: "Ready for Editing" },
  { key: "ready_for_release", label: "Ready for Release" },
  { key: "live", label: "Live" },
];

export { LIFECYCLE_STEPS };
export type { LifecycleStep, LifecycleStepperProps };

export function LifecycleStepper<TStatus extends string = ContentStatus>({
  steps,
  currentStatus,
  onAdvance,
  onRevoke,
  busy = false,
  disabled = false,
  canSetStatus,
}: LifecycleStepperProps<TStatus>) {
  const currentIdx = steps.findIndex(s => s.key === currentStatus);
  const nextStep = currentIdx < steps.length - 1 ? steps[currentIdx + 1] : null;
  const firstStep = currentIdx > 0 ? steps[0] : null;

  // Each button targets a different status, so gate them independently: the
  // backend authorises the PATCH by the target it would set.
  const advanceBlocked = Boolean(nextStep && canSetStatus && !canSetStatus(nextStep.key));
  const revokeBlocked = Boolean(firstStep && canSetStatus && !canSetStatus(firstStep.key));

  return (
    <div className={styles.stageStrip}>
      <div className={styles.stageSteps}>
        {steps.map((step, idx) => (
          <div key={step.key} className={styles.stageStepItem}>
            <span
              className={`${styles.stagePill} ${
                idx < currentIdx ? styles.stageDone : idx === currentIdx ? styles.stageActive : ""
              }`}
            >
              {step.label}
            </span>
            {idx < steps.length - 1 && (
              <span className={styles.stageSeparator} aria-hidden="true">
                →
              </span>
            )}
          </div>
        ))}
      </div>
      <div className={styles.stageActions}>
        {firstStep && onRevoke && (
          <button
            type="button"
            className={`${styles.stageAction} ${styles.stageActionMuted}`}
            onClick={() => onRevoke(firstStep.key)}
            disabled={busy || disabled || revokeBlocked}
            title={
              revokeBlocked
                ? NO_STATUS_PERMISSION_MESSAGE
                : disabled
                  ? "Save your changes first"
                  : undefined
            }
          >
            Revoke
          </button>
        )}
        {nextStep && onAdvance && (
          <button
            type="button"
            className={styles.stageAction}
            onClick={() => onAdvance(nextStep.key)}
            disabled={busy || disabled || advanceBlocked}
            title={
              advanceBlocked
                ? NO_STATUS_PERMISSION_MESSAGE
                : disabled
                  ? "Save your changes first"
                  : undefined
            }
          >
            {busy ? "Saving…" : (nextStep.actionLabel ?? `Mark ${nextStep.label}`)}
          </button>
        )}
      </div>
    </div>
  );
}
